import {
  useLocalRuntime,
  type ChatModelAdapter,
  type ChatModelRunOptions,
} from "@assistant-ui/react"
import { usePageContext } from "./use-page-context"
import { useChatPanel } from "./use-chat-panel"
import type { ToolCallData } from "../types/assistant.types"
import {
  getAuthToken,
  postSseRequest,
  readSseEvents,
  type ParsedEvent,
} from "@/lib/api/sse"

// Re-export for callers that previously imported these from this module.
export { getAuthToken, readSseEvents }
export type { ParsedEvent }

/** Toggle from DevTools console with
 *  ``localStorage.setItem('chat:debug', '1')`` (and reload). Logs each
 *  SSE event received and each adapter yield — useful for chasing
 *  rendering / streaming issues. Off by default in production. */
function debugLog(scope: string, ...args: unknown[]): void {
  try {
    if (localStorage.getItem("chat:debug") !== "1") return
  } catch {
    return
  }
  console.log(`[chat:${scope}]`, ...args)
}

function extractText(message: ChatModelRunOptions["messages"][number]): string {
  const parts: string[] = []
  for (const part of message.content as readonly { type: string; text?: string }[]) {
    if (part.type === "text" && part.text) parts.push(part.text)
  }
  return parts.join("\n")
}

export type StreamContent = Array<
  | { type: "text"; text: string }
  | { type: "data"; name: string; data: unknown }
>

export async function postChatRequest(
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Response> {
  return postSseRequest("/api/issues/chat/", body, signal)
}

export function useChatRuntime() {
  const pageContext = usePageContext()
  const threadId = useChatPanel((s) => s.threadId)
  const setThreadId = useChatPanel((s) => s.setThreadId)
  const setSubmitMode = useChatPanel((s) => s.setSubmitMode)
  const markAssistantReplied = useChatPanel((s) => s.markAssistantReplied)
  const pendingAttachments = useChatPanel((s) => s.pendingAttachments)
  const removeAttachments = useChatPanel((s) => s.removeAttachments)
  const pendingReferences = useChatPanel((s) => s.pendingReferences)
  const clearReferences = useChatPanel((s) => s.clearReferences)

  const adapter: ChatModelAdapter = {
    async *run({ messages, abortSignal }) {
      const last = messages[messages.length - 1]
      const userMessage = extractText(last)
      debugLog(
        "run-start",
        `userMessageLen=${userMessage.length}`,
        `threadId=${threadId}`,
      )

      // A normal user-typed turn resets the submit-mode arming — the user
      // has supplied new context; the next Submit click re-evaluates.
      setSubmitMode("idle")

      // Snapshot the IDs being submitted this turn. Clearing
      // ``pendingAttachments`` is deferred to the SSE
      // ``attachments_linked`` event — emitted by the backend right
      // after it commits the user message + attachment linking, which
      // happens inside the streaming generator (``fetch`` resolves on
      // response *headers*, before any of that runs). Clearing on
      // header-receipt would lose the IDs to a generator-thrown
      // validation error; clearing only on ``done`` would re-stage
      // already-linked IDs if the AI fails after persistence and the
      // user retries.
      const attachmentIds = pendingAttachments.map((a) => a.id)

      // Strip the UI-only ``auto`` flag and ``label`` (the backend
      // re-derives a label from the looked-up entity) before sending.
      const referencesPayload = pendingReferences.map((r) => ({
        type: r.type,
        id: r.id,
        order_id: r.order_id,
      }))

      const response = await postChatRequest(
        {
          user_message: userMessage,
          thread_id: threadId,
          context_order_id: pageContext.orderId,
          context_project_id: pageContext.projectId,
          context_page: pageContext.path,
          intent: "chat",
          attachment_ids: attachmentIds,
          references: referencesPayload,
        },
        abortSignal ?? undefined,
      )

      // Seed the content with a single empty text part. Two reasons:
      //
      // 1. ``LocalRuntime`` from assistant-ui occasionally fails to
      //    paint a streamed message whose only content is a data part
      //    (e.g. when the model emits ONLY a confirm UI marker with no
      //    surrounding prose). The persisted message renders fine on
      //    reload via ``reset()``, but during streaming the bubble
      //    shows up empty. Keeping an empty text part as the first
      //    entry mirrors the text+data shape that the persisted-load
      //    path normally produces and unblocks the data part's render.
      // 2. The seed gives the existing ``currentTextIndex`` logic a
      //    fixed initial slot — text deltas grow it; tool/UI parts
      //    push after it and reset the index so the next delta starts
      //    a fresh text part.
      //
      // The empty text part is invisible and harmless if the AI also
      // emits prose (the prose just appends to it).
      const content: StreamContent = [{ type: "text", text: "" }]
      let currentTextIndex: number | null = 0
      const toolCallIndexById = new Map<string, number>()

      let yieldCount = 0
      const summarizeContent = (parts: StreamContent): string =>
        parts
          .map((p) =>
            p.type === "text"
              ? `text(${p.text.length})`
              : `data(${p.name})`,
          )
          .join(",")
      for await (const event of readSseEvents(response)) {
        debugLog("sse-event", event.type, event)
        if (event.type === "text_delta" && event.text) {
          if (currentTextIndex === null) {
            currentTextIndex = content.length
            content.push({ type: "text", text: event.text })
          } else {
            const existing = content[currentTextIndex]
            if (existing.type === "text") {
              content[currentTextIndex] = {
                type: "text",
                text: existing.text + event.text,
              }
            }
          }
          yieldCount++
          debugLog("yield", yieldCount, summarizeContent(content))
          yield { content: [...content] }
        } else if (event.type === "tool_call_start") {
          currentTextIndex = null
          const toolData: ToolCallData = {
            id: String(event.id ?? ""),
            name: String(event.name ?? ""),
            args: (event.args as Record<string, unknown>) ?? {},
            status: "in_flight",
          }
          toolCallIndexById.set(toolData.id, content.length)
          content.push({ type: "data", name: "tool_call", data: toolData })
          yieldCount++
          debugLog("yield", yieldCount, summarizeContent(content))
          yield { content: [...content] }
        } else if (event.type === "tool_call_done") {
          const id = String(event.id ?? "")
          const idx = toolCallIndexById.get(id)
          if (idx !== undefined) {
            const existing = content[idx]
            if (existing.type === "data" && existing.name === "tool_call") {
              const ok = event.ok !== false
              const updated: ToolCallData = {
                ...(existing.data as ToolCallData),
                status: ok ? "done" : "error",
              }
              content[idx] = { type: "data", name: "tool_call", data: updated }
              yieldCount++
          debugLog("yield", yieldCount, summarizeContent(content))
          yield { content: [...content] }
            }
          }
        } else if (event.type === "ui_component") {
          currentTextIndex = null
          const { type, ...componentData } = event
          content.push({
            type: "data",
            name: componentData.component as string,
            data: componentData,
          })
          yieldCount++
          debugLog("yield", yieldCount, summarizeContent(content))
          yield { content: [...content] }
        } else if (event.type === "attachments_linked") {
          // Backend has committed the user message and bound *these*
          // IDs to it. Drop only the acknowledged subset — files the
          // user staged during the stream are not in this turn and
          // must survive into the next one.
          const ackIds = (event.attachment_ids as number[] | undefined) ?? attachmentIds
          if (ackIds.length) removeAttachments(ackIds)
        } else if (event.type === "done") {
          debugLog(
            "done",
            `yields=${yieldCount}`,
            `final=${summarizeContent(content)}`,
          )
          if (event.thread_id != null) setThreadId(event.thread_id)
          else if (event.issue_id != null) setThreadId(event.issue_id)
          markAssistantReplied()
          // Defensive: if the backend didn't emit ``attachments_linked``
          // (older protocol) but did finish, the staged IDs are bound
          // to the just-saved message. Remove only the IDs that were
          // part of *this* request, leaving anything staged mid-stream
          // intact for the next turn.
          if (attachmentIds.length) removeAttachments(attachmentIds)
          // References are turn-scoped: the backend persists them onto
          // the user's just-saved ChatMessage, and ``reference_hints``
          // re-emits the full set across the conversation each turn. So
          // we drop pending chips after each successful turn — the user
          // can re-add them if they're still relevant to the next turn.
          if (referencesPayload.length) clearReferences()
        } else if (event.type === "error") {
          // Surface the failure inside the assistant bubble so it isn't
          // a mysterious empty placeholder. Replace any partial content
          // with a friendly message. Leave ``pendingAttachments``
          // untouched so the user can retry with the same files.
          content.length = 0
          content.push({
            type: "text",
            text:
              event.message ||
              "Sorry — something went wrong on my side. Please try again, or rephrase your message.",
          })
          yieldCount++
          debugLog("yield", yieldCount, summarizeContent(content))
          yield { content: [...content] }
          markAssistantReplied()
          return
        }
      }
    },
  }

  return useLocalRuntime(adapter)
}
